import React from 'react';

import { useConfiguration } from '../../../context/configurationContext';
import { useIntl } from '../../../util/reactIntl';

import SortByPlain from './SortByPlain';
import SortByPopup from './SortByPopup';

import css from './SortBy.module.css';

/**
 * Filter sort options based on active category and keyword context.
 *
 * Rules:
 *   - Sessioner (any keyword state): return [] — dropdown hidden entirely
 *   - Non-sessioner, no keyword: return only 'nearestDate'
 *   - Non-sessioner, keyword active: return 'nearestDate' and 'relevance'
 *   - '' (Rekommenderat) is never returned — reserved for a future recommendation engine
 *
 * @param {Array<Object>} options - already-translated option objects with { key, label, ... }
 * @param {string|null} activeCategory - e.g. 'sessioner', 'event', 'retreat', or null
 * @param {string} activeKeyword - current keyword string (empty string = no keyword)
 * @returns {Array<Object>}
 */
export const filterSortOptions = (options, activeCategory, activeKeyword) => {
  if (activeCategory === 'sessioner') return [];

  const hasKeyword = !!(activeKeyword && activeKeyword.trim());
  return options.filter(option => {
    if (option.key === '') return false; // Rekommenderat hidden until recommendation engine exists
    if (option.key === 'relevance') return hasKeyword;
    return true;
  });
};

/**
 * SortBy component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.sort] - The sort
 * @param {boolean} [props.showAsPopup] - Whether to show as popup
 * @param {boolean} props.isConflictingFilterActive - Whether the conflicting filter is active
 * @param {boolean} [props.hasConflictingFilters] - Whether there are filters that could conflict
 * @param {'mobile' | 'desktop'} [props.mode] - The mode
 * @param {string|null} [props.activeCategory] - Active category (e.g. 'sessioner', 'event')
 * @param {string} [props.activeKeyword] - Active keyword search string
 * @returns {JSX.Element}
 */
const SortBy = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const {
    sort,
    showAsPopup = false,
    selectedFilters,
    isConflictingFilterActive,
    hasConflictingFilters,
    mode = 'desktop',
    activeCategory = null,
    activeKeyword = '',
    ...rest
  } = props;

  const { relevanceKey, queryParamName } = config.search.sortConfig;

  const mobileClassesMaybe =
    mode === 'mobile'
      ? {
          rootClassName: css.sortBy,
          menuLabelRootClassName: css.sortByMenuLabel,
        }
      : {
          className: css.sortByDesktop,
          menuLabelRootClassName: css.sortByMenuLabel,
        };

  const isConflictingFilterSetAndActive = hasConflictingFilters && !isConflictingFilterActive;

  const translatedOptions = config.search.sortConfig.options.map(option => {
    const longLabelMaybe = option?.labelTranslationKeyLong
      ? { longLabel: intl.formatMessage({ id: option.labelTranslationKeyLong }) }
      : {};
    const translatedOption = option?.labelTranslationKey
      ? { key: option.key, label: intl.formatMessage({ id: option.labelTranslationKey }), ...longLabelMaybe }
      : option;
    const isRelevance = option.key === relevanceKey;
    return {
      ...translatedOption,
      disabled:
        (isRelevance && isConflictingFilterSetAndActive) ||
        (!isRelevance && isConflictingFilterActive),
    };
  });

  const options = filterSortOptions(translatedOptions, activeCategory, activeKeyword);

  if (options.length === 0) return null;

  const defaultValue = '';
  const initialValue =
    hasConflictingFilters && !isConflictingFilterActive
      ? relevanceKey
      : sort || defaultValue;
  const componentProps = {
    urlParam: queryParamName,
    label: intl.formatMessage({ id: 'SortBy.heading' }),
    options,
    initialValue,
    ...mobileClassesMaybe,
    ...rest,
  };
  return showAsPopup ? <SortByPopup {...componentProps} /> : <SortByPlain {...componentProps} />;
};

export default SortBy;
